/**
 * Poster mode: what the page is when the visitor has asked for no animation.
 *
 * `prefers-reduced-motion: reduce` is not a request to hide the scene. It is a request not to
 * *move* it, and the distinction is the whole design here: the courtyard is still drawn, the wall
 * still carries its damage, the zombies still stand at their slots in the windup pose of
 * `bang_wall` — and then the ticker stops and none of it moves again. A square that showed nothing
 * would be telling a visitor with a vestibular disorder less about the outage than the pill above
 * it does, which is the one thing this scene exists not to do.
 *
 * This module owns *whether* that is the mode, and nothing about how it is drawn. Three inputs,
 * and the order between them is the contract:
 *
 *   1. The stored "Enable animations anyway" choice, which only ever turns motion *on*. There is
 *      deliberately no stored way to turn it off: someone who wants less motion has an OS setting
 *      that every site honours, and a second switch here that could contradict it would be a
 *      setting the visitor has to find twice.
 *   2. `?motion=reduce` / `?motion=animate`, which *stands in for the system preference* rather
 *      than outranking everything. A Playwright spec cannot set an OS preference and a bug report
 *      needs a link that reproduces — but a URL that also overrode the visitor's own choice would
 *      make the header toggle untestable and, worse, would be a link that takes a setting away
 *      from whoever opens it.
 *   3. `matchMedia('(prefers-reduced-motion: reduce)')`, when the URL says nothing.
 *
 * Every storage access is wrapped: Safari in private browsing throws on `localStorage` access
 * itself, and a preference lookup may not be the thing that stops the canvas from mounting.
 */

const STORAGE_KEY = 'mha:motion'
const QUERY = '(prefers-reduced-motion: reduce)'

/** `animate` is the visitor's explicit override; `system` means "whatever the OS says". */
export type MotionPreference = 'system' | 'animate'

type Listener = () => void

const listeners = new Set<Listener>()

/**
 * Cached rather than read per call.
 *
 * `posterMode()` is asked on every mount and by the header on every render, and both a media
 * query and a storage read are synchronous main-thread work with a layout-adjacent cost. The
 * cache is invalidated by the two things that can change the answer, both of which notify.
 */
let stored: MotionPreference | null = null

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    // Private browsing, or a Chromium with site data blocked. Not a reason to fail to render.
    return null
  }
}

export function motionPreference(): MotionPreference {
  if (stored !== null) return stored
  try {
    stored = storage()?.getItem(STORAGE_KEY) === 'animate' ? 'animate' : 'system'
  } catch {
    stored = 'system'
  }
  return stored
}

/**
 * Remember that this visitor wants the animation anyway (or stop remembering it).
 *
 * Persisted, because the alternative is asking somebody to press the same button on every visit —
 * and the visitors who press it are by definition the ones whose OS setting will still be on next
 * time.
 */
export function setMotionPreference(preference: MotionPreference): void {
  stored = preference
  try {
    const store = storage()
    if (preference === 'animate') store?.setItem(STORAGE_KEY, 'animate')
    else store?.removeItem(STORAGE_KEY)
  } catch {
    // A preference that cannot be written is still applied for this visit.
  }
  for (const listener of listeners) listener()
}

/**
 * The OS preference, defensively.
 *
 * jsdom implements `matchMedia` only when a test installs it, and an exception here would take the
 * whole canvas down over a preference query. Not knowing means "animate", which is the behaviour
 * every browser that does answer the query already gives us.
 */
export function systemPrefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  try {
    return window.matchMedia(QUERY).matches
  } catch {
    return false
  }
}

/** `?motion=reduce` forces the poster on, `?motion=animate` forces it off, absent defers. */
function fromSearch(search: string): boolean | null {
  const value = new URLSearchParams(search).get('motion')
  if (value === 'reduce') return true
  if (value === 'animate') return false
  return null
}

/**
 * Should the scene render one static frame and stop?
 *
 * `search` defaults to the live location so callers that have no opinion do not have to reach for
 * `window` themselves; the parameter exists so the tests do not have to fake one.
 */
export function posterMode(search: string = locationSearch()): boolean {
  if (motionPreference() === 'animate') return false
  return systemAsksForReducedMotion(search)
}

/**
 * What the *system* is asking for, with `?motion=` standing in for it.
 *
 * Split out from `posterMode` because two callers need this half on its own: the header toggle,
 * which is shown exactly when there is a preference to override, and `posterMode` itself.
 */
export function systemAsksForReducedMotion(search: string = locationSearch()): boolean {
  return fromSearch(search) ?? systemPrefersReducedMotion()
}

function locationSearch(): string {
  return typeof window === 'undefined' ? '' : window.location.search
}

/**
 * Whether the "Enable animations anyway" control has anything to offer.
 *
 * Shown only to visitors whose system asked for reduced motion: a toggle labelled "enable
 * animations" on a page that is already animating is a control that does nothing, and a header
 * carries four other things already.
 */
export function motionToggleApplies(search: string = locationSearch()): boolean {
  return systemAsksForReducedMotion(search)
}

/**
 * Subscribe to changes of the answer. Returns an unsubscribe.
 *
 * Both sources are watched: the header toggle (which calls `setMotionPreference`) and the OS
 * setting itself, because a visitor who turns reduced motion on while the tab is open has asked
 * the page to stop moving now rather than on the next reload.
 */
export function onMotionChange(listener: Listener): () => void {
  listeners.add(listener)
  const media = mediaList()
  media?.addEventListener('change', invalidate)
  return () => {
    listeners.delete(listener)
    media?.removeEventListener('change', invalidate)
  }
}

function mediaList(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null
  try {
    const list = window.matchMedia(QUERY)
    return typeof list.addEventListener === 'function' ? list : null
  } catch {
    return null
  }
}

function invalidate(): void {
  for (const listener of listeners) listener()
}

/** Test seam: a module singleton set by one spec must not leak into the next. */
export function resetMotionPreference(): void {
  stored = null
  listeners.clear()
}
